import { addAlpha, getCSSVariableValue } from 'utils/theme';

const buttonStyles = {
  textDecoration: 'none',
  transition: '.3s ease-in all',
  borderRadius: 'button',
  lineHeight: 'button',
  fontWeight: 'button',
  fontSize: '14px',
  p: 4,
  outline: 'none',
  display: 'inline-flex',
  alignItems: 'center',
  cursor: 'pointer',
  userSelect: 'none',
  ':disabled': {
    pointerEvents: 'none'
  }
};

const simpleButtonStyles = {
  lineHeight: 'button',
  fontWeight: 'button',
  fontSize: '14px',
  px: '24px',
  py: '10px'
};

const cardStyles = {
  borderWidth: 2,
  borderRadius: 'default',
  borderStyle: 'solid',
  boxShadow: 'default'
};

const linkStyles = {
  color: 'primary',
  cursor: 'pointer'
};

export const theme = {
  // ie11 doesn't support custom properties
  useCustomProperties: false,
  //      0, 1, 2, 3, 4,  5,  6,  7,  8   9,  10,  11,  12
  space: [0, 4, 6, 8, 12, 16, 24, 32, 40, 64, 128, 256, 512],
  fonts: {
    // body: 'Picsart-Fonts, -apple-system, BlinkMacSystemFont, \'Segoe UI\', Roboto, Oxygen-Sans, Ubuntu, Cantarell, Helvetica Neue, Helvetica, Arabic, sans-serif',
    // heading: 'Picsart-Fonts, -apple-system, BlinkMacSystemFont, \'Segoe UI\', Roboto, Oxygen-Sans, Ubuntu, Cantarell, Helvetica Neue, Helvetica, Arabic, sans-serif',
    monospace: 'Menlo, monospace'
  },
  //          0, 1,  2,  3,  4,  5,  6,  7,  8   9
  fontSizes: [0, 11, 14, 17, 20, 24, 32, 48, 64, 96],
  fontWeights: {
    body: 400,
    heading: 500,
    bold: 600,
    button: 600
  },
  lineHeights: {
    body: 1.5,
    heading: 1.125,
    button: 1.6,
    sparse: 2.5
  },
  colors: {
    body: 'var(--x-bg-8)',
    text: 'var(--x-social-text-color-action)',
    background: 'var(--x-bg-1)',
    primary: 'var(--x-accent-1-2)',
    secondary: 'var(--x-accent-1-3)',
    tertiary: 'var(--x-accent-2-2)',
    tertiary2: 'var(--x-accent-1-action)',
    error: 'var(--x-status-error)',
    error2: 'var(--x-status-error-action)',
    muted: '#d8e4fa',
    muted3: 'var(--x-text-4)',
    transparent: 'rgba(0,0,0,0)',
    overlay: 'rgba(0,0,0,0.8)',
    grey1: 'var(--x-bg-16)',
    grey2: 'var(--x-bg-4)',
    grey3: 'var(--x-text-4)',
    grey4: 'var(--x-text-7)',
    grey6: 'var(--x-text-8)',
    grey7: 'var(--x-bg-12)',
    grey8: 'var(--x-bg-12)',
    grey9: 'var(--x-text-18)',
    grey10: 'var(--x-text-4)',
    black: 'var(--x-text-color-1)',
    lilac: 'var(--x-accent-2-1)',
    lilac2: 'var(--x-accent-1-1)',
    gold: '#FDA513'
  },
  shadows: {
    default: '0 0 8px rgba(0, 0, 0, 0.125)',
    primary: '0 0 8px rgba(58, 118, 232, 0.75)'
  },
  radii: {
    default: 6,
    button: 8,
    images: 8,
    alert: 4,
    xs: 2,
    modal: 14
  },
  zIndices: {
    notifier: 101,
    modal: 100,
    popup: 50,
    closeTag: 14,
    toolPanel: 10,
    toolBar: 12,
    selectionBorder: 2,
    sceneCardHeader: 1,
    uploadScene: 10,
    arrow: 10,
    floatingSettings: 10
  },
  sizes: [0, 700],
  links: {
    body: {
      ...linkStyles
    },
    bold: {
      ...linkStyles,
      fontWeight: '600'
    }
  },
  text: {
    body: {
      color: 'text',
      fontSize: '12px',
      lineHeight: 'body',
      mb: 2
    },
    panelHeading: {
      fontSize: '12px',
      fontWeight: 'heading',
      lineHeight: 1.2,
      mb: 4
    },
    medium: {
      fontSize: '14px',
      fontWeight: 'bold',
      lineHeight: '17px'
    },
    modalBody: {
      color: 'text4',
      fontSize: '14px',
      lineHeight: 'body',
      mb: 5
    },
    popupHeading: {
      fontSize: '18px',
      fontWeight: 'bold',
      lineHeight: '22px',
      mt: 1,
      mb: '8px'
    },
    h3: {
      fontSize: 3,
      lineHeight: 'body',
      mb: 2
    },
    bullet: {
      fontSize: '14px',
      lineHeight: '24px',
      fontWeight: 'normal'
    },
    bulletSmall: {
      color: 'text',
      fontSize: '12px',
      lineHeight: 'body'
    },
    icon: {
      fontSize: 3,
      lineHeight: 'body'
    }
  },
  buttons: {
    primary: {
      ...buttonStyles,
      ...simpleButtonStyles,
      bg: 'primary',
      color: 'background',
      svg: {
        fill: 'background',
        transition: buttonStyles.transition
      },
      '&:hover': {
        bg: 'lilac2'
      },
      '&:active': {
        bg: 'primary'
      },
      '&:disabled': {
        opacity: 0.7,
        cursor: 'default'
      }
    },
    primaryCentered: {
      ...buttonStyles,
      ...simpleButtonStyles,
      bg: 'tertiary',
      color: 'background',
      svg: {
        fill: 'background',
        transition: buttonStyles.transition
      },
      '&:hover': {
        bg: '#4E00CC'
      },
      '&:active': {
        bg: 'primary'
      },
      '&:disabled': {
        opacity: 0.7,
        cursor: 'default'
      },
      width: '100%',
      display: 'flex',
      justifyContent: 'center'
    },
    clean: {
      ...buttonStyles,
      ...simpleButtonStyles,
      boxSizing: 'border-box',
      color: 'text',
      bg: 'background',
      borderColor: 'grey3',
      borderWidth: 1,
      borderStyle: 'solid',
      '&:hover': {
        borderColor: 'black'
      },
      '&:disabled': {
        opacity: 0.7,
        cursor: 'default'
      }
    },
    transitionCard: {
      ...buttonStyles,
      borderRadius: '8px',
      boxSizing: 'border-box',
      p: 3,
      paddingTop: '0px',
      fontSize: 1,
      color: 'text',
      bg: 'background',
      borderColor: 'grey3',
      borderWidth: 1,
      borderStyle: 'solid',
      fontWeight: 'heading',
      '&:hover': {
        borderColor: 'grey6'
      },
      '&:disabled': {
        opacity: 0.7,
        cursor: 'default'
      }
    },
    transitionCardActive: {
      ...buttonStyles,
      borderRadius: '8px',
      boxSizing: 'border-box',
      p: 3,
      paddingTop: '0px',
      fontSize: 1,
      color: 'text',
      bg: 'background',
      borderColor: 'primary',
      borderWidth: 1,
      borderStyle: 'solid',
      fontWeight: 'heading'
    },
    secondary: {
      ...buttonStyles,
      boxSizing: 'border-box',
      p: 3,
      fontSize: 1,
      color: 'text',
      bg: 'background',
      borderColor: 'grey3',
      borderWidth: 1,
      borderStyle: 'solid',
      fontWeight: 'heading',
      '&:disabled': {
        opacity: 0.7,
        cursor: 'default'
      },
      '&:hover': {
        color: 'text'
      }
    },
    secondaryActive: {
      ...buttonStyles,
      boxSizing: 'border-box',
      p: 3,
      fontSize: 1,
      color: 'text',
      bg: 'muted',
      borderColor: 'primary',
      borderWidth: 1,
      borderStyle: 'solid',
      fontWeight: 'heading',
      '&:disabled': {
        opacity: 0.7,
        cursor: 'default'
      }
    },
    select: {
      ...buttonStyles,
      boxSizing: 'border-box',
      color: 'text',
      bg: 'background',
      borderColor: 'grey3',
      borderWidth: 1,
      borderStyle: 'solid',
      '&:hover': {
        borderColor: 'black'
      },
      '&:disabled': {
        opacity: 0.7,
        cursor: 'default'
      }
    },
    selectActive: {
      ...buttonStyles,
      boxSizing: 'border-box',
      color: 'text',
      bg: 'background',
      borderColor: 'primary',
      borderWidth: 1,
      borderStyle: 'solid',
      '&:disabled': {
        opacity: 0.7,
        cursor: 'default'
      }
    },
    error: {
      ...buttonStyles,
      ...simpleButtonStyles,
      bg: 'error2',
      color: 'background',
      svg: {
        fill: 'background',
        transition: buttonStyles.transition
      },
      '&:hover': {
        bg: 'error'
      },
      '&:disabled': {
        opacity: 0.7,
        cursor: 'default'
      }
    },
    inverted: {
      ...buttonStyles,
      bg: 'background',
      color: 'primary',
      svg: {
        fill: 'primary',
        transition: buttonStyles.transition
      },
      '&:hover, &:focus, &:active': {
        bg: 'primary',
        color: 'background',
        svg: {
          fill: 'background',
          transition: buttonStyles.transition
        }
      }
    },
    invertedOutlined: {
      ...buttonStyles,
      bg: 'background',
      color: 'text',
      borderStyle: 'solid',
      borderWidth: 1,
      borderColor: 'grey3',
      svg: {
        fill: 'primary',
        transition: buttonStyles.transition
      },
      '&:hover, &:focus, &:active': {
        bg: 'primary',
        borderColor: 'primary',
        color: 'background',
        svg: {
          fill: 'background',
          transition: buttonStyles.transition
        }
      }
    },
    text: {
      ...buttonStyles,
      fontSize: 1,
      bg: 'transparent',
      color: 'text',
      svg: {
        fill: 'text',
        transition: buttonStyles.transition
      },
      '&:hover, &:focus, &:active': {
        bg: 'transparent',
        color: 'primary',
        svg: {
          fill: 'primary',
          transition: buttonStyles.transition
        }
      }
    },
    outlined: {
      ...buttonStyles,
      border: '2px solid',
      bg: 'transparent',
      color: 'primary',
      svg: {
        fill: 'primary',
        transition: buttonStyles.transition
      }
    },
    icon: {
      width: 'auto',
      height: 'auto',
      p: 0,
      outline: 'none',
      cursor: 'pointer',
      svg: {
        transition: '0.3s ease-in fill'
      }
    }
  },
  icons: {
    primary: {
      fill: 'primary'
    },
    secondary: {
      fill: 'secondary'
    },
    tertiary: {
      fill: 'tertiary'
    },
    error: {
      fill: 'error'
    },
    text: {
      fill: 'text'
    },
    inverted: {
      fill: 'background'
    },
    ratioFormat: {
      fill: 'text3'
    }
  },
  cards: {
    primary: {
      ...cardStyles,
      borderColor: 'transparent'
    },
    selected: {
      ...cardStyles,
      borderColor: 'primary'
    }
  },
  close: {
    width: 28,
    height: 28,
    cursor: 'pointer',
    color: 'muted3'
  },
  spinners: {
    '& circle': {
      animationDuration: 1200
    }
  },
  alerts: {
    error: {
      color: 'error',
      bg: 'background',
      borderTop: '1px solid',
      borderRadius: 'alert',
      fontSize: 1,
      fontWeight: 'button',
      lineHeight: 'button'
    }
  },
  images: {
    bordered: {
      borderRadius: 'images'
    }
  },
  styles: {
    progress: {
      mb: 2,
      background: 'transparent',
      height: 3,
      color: 'primary',
      bg: '#DCDDE9',
      borderRadius: 'default'
    },
    hr: {
      color: (thm: any) => `${addAlpha(getCSSVariableValue(thm.colors.grey3), 0.5)}`
    }
  },
  forms: {
    text: {
      px: '16px',
      py: '12px',
      fontSize: '14px',
      lineHeight: '20px',
      bg: 'grey1',
      borderWidth: '1px',
      borderRadius: '8px',
      borderColor: 'grey3',
      borderStyle: 'solid',
      outline: 'none',

      ':focus': {
        borderColor: 'primary'
      },
      '&::placeholder': {
        color: 'grey8'
      }
    }
  }
};

export type Theme = typeof theme;
